import "./Failures.scss";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {Loader} from "rsuite";
import {Common_ControlBlockDto, FailuresOverviewDto, StationFailuresDto} from "../../api";
import JournalApi from "../../services/Journal.api";
import {SharedApi} from "../../services/Shared.api";
import {getApiErrorMessage} from "../../services/ApiErrorMessage";
import {formatDateTime, formatDuration, JournalPeriodKey, PERIODS, toDate} from "../../components/menu/Journal/journal.utils";
import {FAILURE_KINDS, FailureKind, FailureKindKey, KIND_BY_EVENT} from "./Failures.model";

type Online = { isConnected: boolean };

// Сервер отдал прошлую сводку и считает новую — спросим ещё раз через столько
const STALE_RETRY_MS = 8000;

// Превью станции — та же картинка, что в списке устройств
const previewUrl = (block?: Common_ControlBlockDto): string => {
  const path = block?.previewImagePath;
  if (!path) return "clock.svg";
  return path.startsWith("http") ? path : `${window.location.origin}/${path}`;
};

// Что видно о станции в строке и по чему ищем
const stationInfo = (block?: Common_ControlBlockDto) => {
  const cs = block?.clockStation;
  return {
    emchs: block?.clockSystemSerialNumber || cs?.clockSystemSerialNumber || "",
    title: block?.description || cs?.description || "",
    csSerial: cs?.serialNumber || "",
    csModel: cs?.model || "",
    bksSerial: block?.serialNumber || "",
    bksModel: block?.model || block?.article || "",
    firmware: block?.firmwareVersion ? String(block.firmwareVersion) : "",
  };
};

const matches = (block: Common_ControlBlockDto | undefined, query: string): boolean => {
  if (!query) return true;
  if (!block) return false;
  const info = stationInfo(block);
  const text = [info.emchs, info.title, info.csSerial, info.csModel, info.bksSerial, info.bksModel, info.firmware,
    block.clockStation?.firmwareVersion, block.privateDescription].filter(Boolean).join(" ").toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every((word) => text.includes(word));
};

// Страница «Сбои» (только администратор): где и какие были сбои по всем станциям
const Failures = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const setQuery = useCallback((value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("q", value);
    else next.delete("q");
    setSearchParams(next, {replace: true});
  }, [searchParams, setSearchParams]);
  const [period, setPeriod] = useState<JournalPeriodKey>("7");
  const [kind, setKind] = useState<FailureKindKey | null>(null);
  const [overview, setOverview] = useState<FailuresOverviewDto | null>(null);
  const [blocks, setBlocks] = useState<Record<string, Common_ControlBlockDto>>({});
  const [online, setOnline] = useState<Record<string, Online>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [retry, setRetry] = useState(0);
  // «Обновить» — пересчитать на сервере сейчас (один раз)
  const forceRefresh = useRef(false);

  useEffect(() => {
    SharedApi.getControlBlocks()
      .then((list) => setBlocks(Object.fromEntries((list ?? []).filter((x) => x.id).map((x) => [x.id as string, x]))))
      .catch(() => setBlocks({}));
  }, []);

  // Смена периода — новая сводка; «Обновить» — пересчитать сейчас
  useEffect(() => {
    setOverview(null);
  }, [period]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setLoading(true);
    setError(null);
    const refresh = forceRefresh.current;
    forceRefresh.current = false;
    JournalApi.getFailuresOverview(period, refresh)
      .then((data) => {
        if (cancelled) return;
        setOverview(data);
        if (data.isStale) timer = setTimeout(() => setRetry((x) => x + 1), STALE_RETRY_MS);
        const ids = (data.stations ?? []).map((x) => x.controlBlockId).filter((x): x is string => Boolean(x));
        return SharedApi.getControlBlocksStatusBatch(ids).then((statuses) => {
          if (cancelled) return;
          setOnline(Object.fromEntries(statuses.filter((x) => x.id).map((x) => [x.id as string, {isConnected: Boolean(x.isConnected)}])));
        }).catch(() => setOnline({}));
      })
      .catch((e) => !cancelled && setError(getApiErrorMessage(e, "Не удалось загрузить сводку сбоев")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [period, reload, retry]);

  const allStations = useMemo(() => overview?.stations ?? [], [overview]);
  const stations = useMemo(() => allStations.filter((s) => matches(blocks[s.controlBlockId ?? ""], query.trim())),
    [allStations, blocks, query]);

  const totals = useMemo(() => Object.fromEntries(FAILURE_KINDS.map((k) => [
    k.key,
    {
      count: stations.reduce((sum, s) => sum + (s.counters ? k.count(s.counters) : 0), 0),
      stations: stations.filter((s) => s.counters && k.count(s.counters) > 0).length,
    },
  ])) as Record<FailureKindKey, { count: number; stations: number }>, [stations]);

  const firmwareFaults = useMemo(() => stations.reduce((sum, s) => sum + (s.counters?.firmwareFaults ?? 0), 0), [stations]);

  const activeKind = FAILURE_KINDS.find((k) => k.key === kind) ?? null;

  const rows = useMemo(() => {
    if (!activeKind) return stations;
    return [...stations]
      .filter((s) => s.counters && activeKind.count(s.counters) > 0)
      .sort((a, b) => activeKind.count(b.counters!) - activeKind.count(a.counters!));
  }, [stations, activeKind]);

  const withFailures = stations.filter((s) => (s.total ?? 0) > 0).length;

  const openStation = useCallback((station: StationFailuresDto, kindOverride?: FailureKind) => {
    const target = kindOverride ?? activeKind;
    const params = new URLSearchParams({tab: "journal", journal: "system", period});
    if (target) params.set("filter", target.filter);
    navigate(`/devices/${station.controlBlockId}?${params.toString()}`);
  }, [activeKind, navigate, period]);

  return (
    <div className="failures">
      <div className="failures__head">
        <h2 className="failures__title">Сбои по всем станциям</h2>
        <div className="failures__periods">
          {PERIODS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`failures__period${period === item.key ? " failures__period--active" : ""}`}
              onClick={() => setPeriod(item.key)}
            >
              {item.label}
            </button>
          ))}
          <button type="button" className="failures__period failures__period--refresh" disabled={loading}
                  onClick={() => {
                    forceRefresh.current = true;
                    setReload((x) => x + 1);
                  }}>
            Обновить
          </button>
        </div>
      </div>

      <div className="failures__search">
        <input
          type="search"
          className="failures__search-input"
          placeholder="Поиск: ЭМЧС, № ЧС или БКС, модель, прошивка, описание…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <button type="button" className="failures__search-clear" aria-label="Очистить поиск" onClick={() => setQuery("")}>
            ✕
          </button>
        )}
      </div>

      {overview && (
        <div className="failures__summary">
          <span>Станций со сбоями: <b>{withFailures}</b> из {overview.stationsTotal ?? 0}</span>
          {query.trim() && <span>найдено: <b>{stations.length}</b></span>}
          <span className="failures__muted">
            {overview.isStale ? `данные на ${formatDateTime(overview.calculatedAt)}, обновляю…` : `данные на ${formatDateTime(overview.calculatedAt)}`}
          </span>
          {activeKind && (
            <button type="button" className="failures__reset" onClick={() => setKind(null)}>
              Показать все виды
            </button>
          )}
        </div>
      )}

      <div className="failures__tiles">
        {FAILURE_KINDS.map((k) => {
          const t = totals[k.key];
          const empty = !t || t.count === 0;
          return (
            <button
              key={k.key}
              type="button"
              disabled={empty}
              className={`failures__tile failures__tile--${k.tone}${kind === k.key ? " failures__tile--active" : ""}${empty ? " failures__tile--empty" : ""}`}
              onClick={() => setKind(kind === k.key ? null : k.key)}
            >
              <span className="failures__tile-label">{k.label}</span>
              <span className="failures__tile-count">{t?.count ?? 0}</span>
              <span className="failures__tile-note">
                {empty ? "не было" : `на ${t.stations} ${plural(t.stations, "станции", "станциях", "станциях")}`}
                {k.key === "restarts" && firmwareFaults > 0 && `, сбой прошивки: ${firmwareFaults}`}
              </span>
            </button>
          );
        })}
      </div>

      {loading && !overview && <div className="failures__state"><Loader content="Собираю сбои по станциям…"/></div>}
      {!loading && error && <div className="failures__state failures__state--error">{error}</div>}
      {!error && overview && rows.length === 0 && (
        <div className="failures__state">
          {query.trim() && allStations.length > 0 ? `По запросу «${query.trim()}» станций со сбоями нет` : "За этот период сбоев не было"}
        </div>
      )}

      {!error && rows.length > 0 && (
        <div className="failures__table">
          <div className="failures__row failures__row--head">
            <span>Станция</span>
            <span>Сбоев</span>
            <span>По видам</span>
            <span>Последний сбой</span>
            <span>Сейчас</span>
          </div>
          {rows.map((station) => (
            <StationRow
              key={station.controlBlockId}
              station={station}
              block={blocks[station.controlBlockId ?? ""]}
              online={online[station.controlBlockId ?? ""]}
              activeKind={activeKind}
              onOpen={openStation}
            />
          ))}
        </div>
      )}
      {rows.length > 0 && (
        <div className="failures__hint">Нажмите на станцию, чтобы открыть её журнал «Состояние системы» за этот период.</div>
      )}
    </div>
  );
};

type StationRowProps = {
  station: StationFailuresDto;
  block?: Common_ControlBlockDto;
  online?: Online;
  activeKind: FailureKind | null;
  onOpen: (station: StationFailuresDto, kind?: FailureKind) => void;
};

const StationRow = ({station, block, online, activeKind, onOpen}: StationRowProps) => {
  const counters = station.counters;
  const kinds = counters ? FAILURE_KINDS.filter((k) => k.count(counters) > 0) : [];
  const last = station.lastFailure;
  const lastKind = last ? FAILURE_KINDS.find((k) => k.key === KIND_BY_EVENT[last.kind ?? ""]) : undefined;
  const info = stationInfo(block);
  const emchs = info.emchs;
  const title = info.title || "Без названия";
  const cs = [info.csSerial ? `ЧС ${info.csSerial}` : "ЧС —", info.csModel].filter(Boolean).join(" · ");
  const bks = [info.bksModel ? `БКС ${info.bksModel}` : "БКС —", info.bksSerial ? `№ ${info.bksSerial}` : "",
    info.firmware ? `прошивка ${info.firmware}` : "прошивка —"].filter(Boolean).join(" · ");
  const offlineSince = toDate(block?.lastOnlineTime);
  const ongoing = (station.ongoing ?? []).filter((x) => x.kind !== "Connection");

  return (
    <div
      role="button"
      tabIndex={0}
      className="failures__row"
      onClick={() => onOpen(station)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(station)}
    >
      <span className="failures__station">
        <img className="failures__preview" src={previewUrl(block)} alt="" loading="lazy"
             onError={(e) => {
               if (!e.currentTarget.src.endsWith("/clock.svg")) e.currentTarget.src = "clock.svg";
             }}/>
        <span className="failures__station-text">
        <b>{emchs ? `ЭМЧС ${emchs}` : "ЭМЧС —"}</b>
        <span>{title}</span>
        <small>{cs}</small>
        <small>{bks}</small>
        </span>
      </span>
      <span className="failures__total">
        {activeKind && counters ? activeKind.count(counters) : station.total ?? 0}
        {activeKind && <small> из {station.total ?? 0}</small>}
      </span>
      <span className="failures__kinds">
        {kinds.map((k) => (
          <button
            key={k.key}
            type="button"
            className={`failures__chip failures__chip--${k.tone}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpen(station, k);
            }}
          >
            {k.short} {k.count(counters!)}
          </button>
        ))}
        {kinds.length === 0 && <span className="failures__muted">—</span>}
      </span>
      <span className="failures__last">
        {last ? (
          <>
            <small>{formatDateTime(last.time)}{lastKind ? ` · ${lastKind.label.toLowerCase()}` : ""}</small>
            <span>{last.source ? `${last.source}: ` : ""}{last.text}</span>
          </>
        ) : <span className="failures__muted">—</span>}
      </span>
      <span className="failures__now">
        {online?.isConnected === false ? (
          <span className="failures__bad">
            нет связи{offlineSince ? ` ${formatDuration(Date.now() - offlineSince.getTime())}` : ""}
          </span>
        ) : online?.isConnected ? (
          <span className="failures__ok">на связи</span>
        ) : <span className="failures__muted">—</span>}
        {ongoing.slice(0, 2).map((x, i) => (
          <small key={i} className="failures__bad">идёт: {x.text}</small>
        ))}
      </span>
    </div>
  );
};

const plural = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};

export default Failures;
